import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useIsWindowWidthBelow } from '../../hooks/useWindowSize';
import { MOBILE_SIZE } from '../../common/constants';

import delivery from '../../../assets/icons/element-reassuranc-livraison.svg';
import responsible from '../../../assets/icons/element-reassurance-responsable.svg';
import reference from '../../../assets/icons/element-reassurance-reference.svg';
import accompaniement from '../../../assets/icons/element-reassurance-accompagnement.svg';
import enterprise from '../../../assets/icons/element-reassurance-entreprise-montpellieraine.svg';

interface ReinsuranceElementProps {
  background: string;
  srcImage: string;
  message: string;
}

interface ReinsuranceProps {
  reinsuranceElements?: ReinsuranceElementProps[];
}

const defaultReinsuranceElements: ReinsuranceElementProps[] = [
  { background: '#27bad0', srcImage: enterprise, message: 'gatsby.home.reinsurance.pioneer' },
  { background: '#fcb74b', srcImage: accompaniement, message: 'gatsby.home.reinsurance.price' },
  { background: '#e93871', srcImage: delivery, message: 'gatsby.home.reinsurance.assistance' },
  { background: '#5d5da2', srcImage: reference, message: 'gatsby.home.reinsurance.express' },
  { background: '#00C882', srcImage: responsible, message: 'gatsby.home.reinsurance.responsible' }
];

export const Reinsurance: React.FC<ReinsuranceProps> = ({ reinsuranceElements = defaultReinsuranceElements }) => {
  const { t } = useTranslation('translations');
  const isMobile = useIsWindowWidthBelow(MOBILE_SIZE);
  const [{ indexTransition, previousIndexTransition }, setIndexTransition] = useState<{
    indexTransition: number;
    previousIndexTransition?: number;
  }>({
    indexTransition: 1,
    previousIndexTransition: undefined
  });

  if (!isMobile)
    return (
      <StyledReinsuranceWrapper>
        {reinsuranceElements.map((elem, index) => (
          <StyledElementWrapper key={`hp-reinsurance-${index}`} background={elem?.background ?? '#FFFFFF'} isMobile={false}>
            {elem?.srcImage && <StyledElementImage src={elem.srcImage} isMobile={false} alt="" />}
            {elem?.message && <StyledElementSpan isMobile={false}>{t(elem.message)}</StyledElementSpan>}
          </StyledElementWrapper>
        ))}
      </StyledReinsuranceWrapper>
    );

  setTimeout(() => {
    setIndexTransition({
      indexTransition: (indexTransition < defaultReinsuranceElements.length ? indexTransition : 0) + 1,
      previousIndexTransition: indexTransition
    });
  }, 3000);

  return (
    <StyledMobileReinsuranceWrapper indexTransition={indexTransition} previousIndexTransition={previousIndexTransition}>
      {reinsuranceElements.map((elem, index) => (
        <StyledElementWrapper key={`hp-reinsurance-${index}`} background={elem?.background ?? '#FFFFFF'} isMobile>
          {elem?.srcImage && <StyledElementImage src={elem.srcImage} alt="" isMobile />}
          {elem?.message && <StyledElementSpan isMobile>{t(elem.message)}</StyledElementSpan>}
        </StyledElementWrapper>
      ))}
    </StyledMobileReinsuranceWrapper>
  );
};

const widthMax = keyframes`
  from {
    width: 4px;
  }
  to {
    width: calc(100% - 36px);
  }
`;

const widthMin = keyframes`
  from {
    width: calc(100% - 36px);
  }
  to {
    width: 4px;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const StyledReinsuranceWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  height: 40px;

  @media (max-width: 1400px), (min-width: 1500px) and (max-width: 1700px) {
    margin-top: 0px;
  }
  @media (max-width: 1024px) {
    flex-wrap: wrap;
  }
`;

const StyledMobileReinsuranceWrapper = styled(StyledReinsuranceWrapper)<{ indexTransition: number; previousIndexTransition: number }>`
  gap: 4px;
  padding: 0px;
  height: 39px;

  > div {
    > img,
    span {
      opacity: 0;
    }
  }
  div:nth-child(${({ previousIndexTransition }) => previousIndexTransition}) {
    animation: ${widthMin} 1s 1;
    > img,
    span {
      animation: ${fadeOut} 500ms 1;
      opacity: 0;
    }
  }
  div:nth-child(${({ indexTransition }) => indexTransition}) {
    animation: ${widthMax} 1s 1;
    width: calc(100% - 36px);
    > img,
    span {
      animation: ${fadeIn} 1500ms 1;
      opacity: 1;
    }
  }
`;

const StyledElementWrapper = styled.div<{ background: string; isMobile: boolean }>`
  height: ${({ isMobile }) => (isMobile ? 31 : 40)}px;
  border-bottom: 3px solid ${({ background }) => background};
  // padding: ${({ isMobile }) => (isMobile ? '8px 0' : '8px 16px')};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  word-break: keep-all;
  width: ${({ isMobile }) => (isMobile ? '4px' : '20%')};
  gap: 4px;
`;

const StyledElementImage = styled.img<{ isMobile: boolean }>`
  height: ${({ isMobile }) => (isMobile ? 24 : 32)}px;
`;

const StyledElementSpan = styled.span<{ isMobile: boolean }>`
  color: #323232;
  font-family: 'StyreneA', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;
  ${({ isMobile }) =>
    isMobile &&
    `
    font-size: 12px;
    line-height: 15px;
  `}
`;
